export enum PackageDuration {
  Days30 = 'days30',
  Days365 = 'days365',
  Perpetual = 'perpetual',
  Custom = 'custom',
}

/** Order the duration chips and the tiered discount rows are rendered in. */
export const packageDurations = [
  PackageDuration.Days30,
  PackageDuration.Days365,
  PackageDuration.Perpetual,
  PackageDuration.Custom,
];

/** How the API stores a variant's length. */
export enum PackageVariantDurationType {
  FixedDays = 'fixed_days',
  Custom = 'custom',
  Perpetual = 'perpetual',
}

/** Duration variant as the frontend holds it, with the API's decimal strings parsed. */
export interface IPackageVariant {
  durationType: PackageVariantDurationType;
  durationDays: number | null;
  discountPercentage: number;
  /** Computed server-side. Perpetual has no day count, so it cannot be derived locally. */
  totalPrice: number;
  archived: boolean;
}

/** Variant as sent to the API. `durationDays` must be omitted for perpetual. */
export interface IProductPackageVariantPayload {
  durationType: PackageVariantDurationType;
  durationDays?: number;
  discountPercentage: number;
  totalPrice: number;
}

/** Number of billable days, or null when the duration has no fixed length (perpetual, or custom with no value yet). */
export const getPackageDurationDays = (duration: PackageDuration, customDays: number | string): number | null => {
  if (duration === PackageDuration.Days30) return 30;
  if (duration === PackageDuration.Days365) return 365;
  if (duration === PackageDuration.Custom) {
    const days = Number(customDays);
    return Number.isFinite(days) && days > 0 ? days : null;
  }
  return null;
};

/** Daily price once the tier's discount is applied. */
export const getEffectiveDailyPrice = (baseDailyPrice: number, discountPercentage: number): number =>
  baseDailyPrice * (1 - discountPercentage / 100);

/** Rounded to cents so floating point artifacts never reach the API. */
const toCurrency = (value: number): number => Math.round(value * 100) / 100;

interface IPackageVariantsInput {
  durations: PackageDuration[];
  customDurationDays: number | string;
  discounts: Partial<Record<PackageDuration, number | string>>;
  baseDailyPrice: number | string;
  perpetualTotalPrice: number | string;
}

/** Builds the `variants` list the API expects from the chips, the custom day count and the pricing table. */
export const getPackageVariantsPayload = ({
  durations,
  customDurationDays,
  discounts,
  baseDailyPrice,
  perpetualTotalPrice,
}: IPackageVariantsInput): IProductPackageVariantPayload[] =>
  packageDurations
    .filter((duration) => durations.includes(duration))
    .map((duration) => {
      const discountPercentage = Number(discounts[duration]) || 0;

      // Perpetual has no day count to multiply, so its total is typed in rather than derived.
      if (duration === PackageDuration.Perpetual) {
        return {
          durationType: PackageVariantDurationType.Perpetual,
          discountPercentage,
          totalPrice: toCurrency(Number(perpetualTotalPrice) || 0),
        };
      }

      const days = getPackageDurationDays(duration, customDurationDays) ?? 0;

      return {
        durationType:
          duration === PackageDuration.Custom
            ? PackageVariantDurationType.Custom
            : PackageVariantDurationType.FixedDays,
        durationDays: days,
        discountPercentage,
        totalPrice: toCurrency(getEffectiveDailyPrice(Number(baseDailyPrice) || 0, discountPercentage) * days),
      };
    });

/**
 * The chips only cover 30 days, 365 days and perpetual, so any other fixed length
 * is shown as a custom duration.
 */
const getVariantDuration = (variant: IPackageVariant): PackageDuration => {
  if (variant.durationType === PackageVariantDurationType.Perpetual) return PackageDuration.Perpetual;
  if (variant.durationDays === 30) return PackageDuration.Days30;
  if (variant.durationDays === 365) return PackageDuration.Days365;
  return PackageDuration.Custom;
};

interface IPackageDurationsState {
  durations: PackageDuration[];
  customDurationDays: number | string;
  discounts: Partial<Record<PackageDuration, number | string>>;
  perpetualTotalPrice: number | string;
}

/** Restores the form state an existing package's variants were built from. Archived variants are historical and skipped. */
export const getPackageDurationsState = (variants: IPackageVariant[]): IPackageDurationsState => {
  const durations: PackageDuration[] = [];
  const discounts: Partial<Record<PackageDuration, number | string>> = {};
  let customDurationDays: number | string = '';
  let perpetualTotalPrice: number | string = '';

  for (const variant of variants) {
    if (variant.archived) continue;

    const duration = getVariantDuration(variant);
    if (durations.includes(duration)) continue;

    durations.push(duration);
    discounts[duration] = variant.discountPercentage;

    if (duration === PackageDuration.Custom) {
      customDurationDays = variant.durationDays ?? '';
    }

    if (duration === PackageDuration.Perpetual) {
      perpetualTotalPrice = variant.totalPrice;
    }
  }

  return { durations, customDurationDays, discounts, perpetualTotalPrice };
};
